import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Radio, Plus, Users, Lock, Globe, Search, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useWalkie, Avatar, notify } from "./WalkieLayout";

export function ChannelRow({ c }) {
  return (
    <Link to={`/walkie/c/${c.id}`} data-testid={`walkie-channel-${c.id}`} className="wt-card rounded-2xl p-3 flex items-center gap-3 hover:border-[#FF2BD6]/50 transition-colors">
      {c.kind === "dm" ? <Avatar p={{ display_name: c.name, avatar_url: c.avatar_url }} size={40} /> : (
        <span className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#FF6A00] to-[#FF2BD6] flex items-center justify-center text-black shrink-0">
          {c.kind === "public" ? <Radio className="w-5 h-5" /> : <Lock className="w-5 h-5" />}
        </span>
      )}
      <div className="flex-1 min-w-0">
        <div className="font-bold truncate">{c.kind === "dm" ? c.name : `#${c.name}`}</div>
        <div className="text-[11px] wt-muted truncate">{c.description || (c.kind === "dm" ? "Private conversation" : `${c.kind} channel`)}</div>
      </div>
      <div className="text-right text-[11px]">
        {c.live_count > 0 && <div className="text-[#39FF14] font-black">● {c.live_count} live</div>}
        <div className="wt-muted flex items-center gap-1 justify-end"><Users className="w-3 h-3" />{c.member_count}</div>
      </div>
    </Link>
  );
}

function CreateChannel({ onDone }) {
  const { me, reloadMe } = useWalkie();
  const [f, setF] = useState({ name: "", description: "", kind: "public", theme: "default" });
  const [busy, setBusy] = useState(false);
  const themes = ["default", ...me.profile.owned.filter((o) => o.startsWith("th_")), ...(me.profile.premium ? ["th_galaxy", "th_holo"] : [])];
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { const r = await api.post("/walkie/channels", f); toast.success("Channel created"); reloadMe(); onDone(r.data.id); } catch (er) { notify.err(er); }
    setBusy(false);
  };
  return (
    <form onSubmit={submit} className="wt-card rounded-2xl p-4 space-y-3" data-testid="walkie-create-form">
      <div className="text-xs wt-muted">You own {me.channels_owned}/{me.channel_limit} channels</div>
      <input required minLength={2} maxLength={40} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Channel name" data-testid="walkie-create-name" className="wt-input w-full rounded-xl px-3 py-2 outline-none" />
      <input maxLength={200} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} placeholder="Description (optional)" className="wt-input w-full rounded-xl px-3 py-2 outline-none" />
      <div className="grid grid-cols-3 gap-2">
        {[["public", "Public", Globe], ["private", "Private", Lock], ["group", "Group", Users]].map(([k, l, I]) => (
          <button type="button" key={k} onClick={() => setF({ ...f, kind: k })} data-testid={`walkie-create-kind-${k}`}
            className={`py-2 rounded-xl text-xs font-bold border flex items-center justify-center gap-1 ${f.kind === k ? "border-[#FF2BD6] text-[#FF2BD6] bg-[#FF2BD6]/10" : "wt-line"}`}><I className="w-3.5 h-3.5" />{l}</button>
        ))}
      </div>
      <select value={f.theme} onChange={(e) => setF({ ...f, theme: e.target.value })} className="wt-input w-full rounded-xl px-3 py-2 text-sm">
        {themes.map((t) => <option key={t} value={t}>{t === "default" ? "Default theme" : t.replace("th_", "") + " theme"}</option>)}
      </select>
      <button disabled={busy} data-testid="walkie-create-submit" className="w-full py-3 rounded-full bg-[#FF2BD6] text-black font-black flex items-center justify-center gap-2">
        {busy && <Loader2 className="w-4 h-4 animate-spin" />} Create channel
      </button>
    </form>
  );
}

export default function WalkieChannels() {
  const nav = useNavigate();
  const [scope, setScope] = useState("public");
  const [q, setQ] = useState("");
  const [rows, setRows] = useState(null);
  const [creating, setCreating] = useState(false);
  const [code, setCode] = useState("");
  useEffect(() => {
    const t = setTimeout(() => api.get("/walkie/channels", { params: { scope, q } }).then((r) => setRows(r.data.channels)).catch(() => setRows([])), 250);
    return () => clearTimeout(t);
  }, [scope, q]);
  const joinCode = () => { const c = code.trim().split("/").pop(); if (c) nav(`/walkie/join/${c}`); };
  return (
    <div className="space-y-4" data-testid="walkie-channels">
      <div className="flex items-center justify-between">
        <h1 className="font-black text-2xl">Channels</h1>
        <button onClick={() => setCreating((v) => !v)} data-testid="walkie-new-channel-btn" className="flex items-center gap-1 text-sm font-black bg-[#FF2BD6] text-black px-4 py-2 rounded-full"><Plus className="w-4 h-4" /> New</button>
      </div>
      {creating && <CreateChannel onDone={(id) => nav(`/walkie/c/${id}`)} />}
      <div className="flex gap-2">
        {[["public", "Discover"], ["mine", "My channels & chats"]].map(([k, l]) => (
          <button key={k} onClick={() => setScope(k)} data-testid={`walkie-scope-${k}`}
            className={`text-xs font-bold px-3 py-1.5 rounded-full border ${scope === k ? "border-[#FF2BD6] text-[#FF2BD6] bg-[#FF2BD6]/10" : "wt-line wt-muted"}`}>{l}</button>
        ))}
      </div>
      <div className="flex gap-2">
        <div className="flex-1 relative"><Search className="w-4 h-4 absolute left-3 top-2.5 wt-muted" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search channels" data-testid="walkie-channel-search" className="wt-input w-full rounded-full pl-9 pr-3 py-2 text-sm outline-none" /></div>
      </div>
      <div className="flex gap-2">
        <input value={code} onChange={(e) => setCode(e.target.value)} placeholder="Paste an invite link or code" data-testid="walkie-invite-input" className="wt-input flex-1 rounded-full px-4 py-2 text-sm outline-none" />
        <button onClick={joinCode} data-testid="walkie-invite-join" className="text-sm font-black px-4 rounded-full bg-[#00F0FF] text-black">Join</button>
      </div>
      {!rows ? <div className="wt-muted text-sm animate-pulse">Loading…</div> : rows.length === 0 ? <div className="wt-muted text-sm">No channels found.</div> : (
        <div className="space-y-2">{rows.map((c) => <ChannelRow key={c.id} c={c} />)}</div>
      )}
    </div>
  );
}

export function WalkieJoin() {
  const { code } = useParams();
  const nav = useNavigate();
  useEffect(() => {
    api.post(`/walkie/invite/${code}`).then((r) => { toast.success(`Joined #${r.data.name}`); nav(`/walkie/c/${r.data.channel_id}`, { replace: true }); })
      .catch((e) => { notify.err(e); nav("/walkie/channels", { replace: true }); });
  }, [code, nav]);
  return <div className="py-16 text-center wt-muted" data-testid="walkie-joining">Joining channel…</div>;
}
